import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Common, PopUpBox } from '../../../components';
import MobileSms from '../../../assets/images/mobile-sms.svg';
import { actionLoginOTP, actionSendClientID, actionSendOTP } from '../../../store/application/actions';
import { numberWithNoCommas } from '../../../utils/common';
import { createApplicationFlowMeta } from '../../../utils/applicationDraft';
import { QUICK_CASH_PRODUCT_CODE } from '../config';
import { clearPreviousApplicationForQuickCash } from '../flowState';
import { buildQuickCashEntryUrl, QUICK_CASH_ROUTES } from '../routes';
import {
  clearQuickCashDraft,
  clearQuickCashPendingPhone,
  commitQuickCashPendingAttribution,
  getQuickCashPendingAttribution,
  getQuickCashPendingPhone,
  saveQuickCashDraft,
} from '../storage';

const COUNT_TIMES = 4;

function OTP() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const phone = location.state?.phone || getQuickCashPendingPhone();
  const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm();
  const [isVisibleTimeOverOTP, setIsVisibleTimeOverOTP] = useState(false);
  const [isVisibleWrongOTP, setIsVisibleWrongOTP] = useState(false);
  const [isVisibleExpiredOTP, setIsVisibleExpiredOTP] = useState(false);
  const [countTimes, setCountTimes] = useState(COUNT_TIMES);
  const [countView, setCountView] = useState(COUNT_TIMES + 1);
  const [resendDisabled, setResendDisabled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!phone) {
      navigate(buildQuickCashEntryUrl(getQuickCashPendingAttribution().session_id), { replace: true });
    }
  }, [navigate, phone]);

  useEffect(() => {
    if (countTimes === 0 && !isVisibleExpiredOTP) {
      setIsVisibleTimeOverOTP(true);
    }
  }, [countTimes, isVisibleExpiredOTP]);

  const onReSendOTP = async () => {
    if (resendDisabled) return;
    if (countTimes === 0) {
      navigate(buildQuickCashEntryUrl(getQuickCashPendingAttribution().session_id));
      return;
    }

    setResendDisabled(true);
    await dispatch(actionSendOTP({ phone, product_code: QUICK_CASH_PRODUCT_CODE }));
    setIsVisibleTimeOverOTP(false);
    setResendDisabled(false);
  };

  const onSubmit = async (data) => {
    const response = await dispatch(actionLoginOTP({
      phone,
      otp: data.otp,
      product_code: QUICK_CASH_PRODUCT_CODE,
    }));

    if (response?.sts_code === 0) {
      // Backend의 문자 예약 교체가 성공한 뒤 기존 신청 키만 지우고,
      // 방금 발급받은 Quick Cash 토큰을 공용 인증 키에 저장한다.
      clearPreviousApplicationForQuickCash();
      localStorage.setItem('access_token', response?.content?.access_token || '');
      localStorage.setItem('application_phone', phone);
      commitQuickCashPendingAttribution();

      const flowMeta = createApplicationFlowMeta({ phone });
      // 신규 토큰을 저장한 뒤 Quick Cash 전용 draft를 생성한다.
      clearQuickCashDraft();
      saveQuickCashDraft({
        meta: flowMeta,
        currentStep: 'consent',
        uiState: { consentAccepted: false },
      });
      clearQuickCashPendingPhone();

      if (process.env.REACT_APP_GA_CODE) {
        ReactGA.initialize(process.env.REACT_APP_GA_CODE, {
          gaOptions: { userId: response?.content?.user_id },
        });
        ReactGA.ga(async (tracker) => {
          await dispatch(actionSendClientID({ client_id: tracker.get('clientId') }));
        });
        ReactGA.set({ user_app_id: response?.content?.user_id, product_code: QUICK_CASH_PRODUCT_CODE });
      }

      navigate(QUICK_CASH_ROUTES.otpSuccess, {
        replace: true,
        state: { phone, path: QUICK_CASH_ROUTES.consent },
      });
      return;
    }

    if (response?.sts_code === 5) {
      setValue('otp', '');
      setIsVisibleWrongOTP(true);
      setCountView((value) => value - 1);
    } else if (response?.sts_code === 6) {
      setCountTimes(0);
      setIsVisibleExpiredOTP(true);
      setIsVisibleTimeOverOTP(false);
    } else if (response?.sts_code === 7) {
      setCountTimes(0);
      setIsVisibleTimeOverOTP(true);
    } else {
      setCountTimes((value) => value - 1);
    }
  };

  return (
    <>
      <Common.Loading isVisible={isSubmitting} />
      <div className="bg-white p-5">
        {isVisibleTimeOverOTP && (
          <PopUpBox.TimeOverOTP onClick={onReSendOTP} number={countTimes}>
            <div className="text-center my-3">
              <button onClick={onReSendOTP} type="button" className="rounded-full text-white text-sm bg-primary px-12 py-3">確定</button>
            </div>
          </PopUpBox.TimeOverOTP>
        )}
        {isVisibleWrongOTP && !isVisibleExpiredOTP && (
          <PopUpBox.Error onClick={() => setIsVisibleWrongOTP(false)} content="">
            <div className="text-center">
              {countView > 0 && <p className="text-gray1 my-6">驗證碼錯誤 <span className="text-primary2">(剩餘{countView}次)</span></p>}
              <button onClick={() => setIsVisibleWrongOTP(false)} type="button" className="rounded-full text-white text-sm bg-primary px-12 py-3">重新輸入</button>
            </div>
          </PopUpBox.Error>
        )}
        {isVisibleExpiredOTP && (
          <PopUpBox.Error onClick={() => setIsVisibleExpiredOTP(false)} content="驗證超時，按此取得新的驗證碼。">
            <div className="text-center">
              <button
                onClick={async () => {
                  await dispatch(actionSendOTP({ phone, product_code: QUICK_CASH_PRODUCT_CODE }));
                  setCountTimes(1);
                  setValue('otp', '');
                  setIsVisibleExpiredOTP(false);
                }}
                type="button"
                className="rounded-full text-white text-sm bg-primary px-14 py-2 mt-3"
              >確定</button>
            </div>
          </PopUpBox.Error>
        )}
        <p className="flex-1 text-2xl text-primary2 text-right font-semibold border-b border-gray2 mb-4 pb-4">安全驗證</p>
        <div className="flex justify-center mb-5"><img src={MobileSms} alt="" /></div>
        <p className="text-gray mb-4 text-center">驗證碼已發送到閣下登記之手提電話號碼</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-12 mb-2">
            <input
              inputMode="numeric"
              {...register('otp', {
                required: true,
                onChange: (event) => setValue('otp', numberWithNoCommas(event?.target?.value)),
              })}
              className="border-b border-primary2 w-full h-10 outline-none px-2 text-center text-primary2"
              placeholder="請在此輸入驗證碼"
              maxLength={6}
            />
            {errors.otp?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>
          <p className="text-8 mb-6 text-center text-gray">未能收到驗證碼？ <button type="button" className={`${!resendDisabled ? 'text-primary' : 'text-gray'} underline`} onClick={onReSendOTP} disabled={resendDisabled}>取得新的驗證碼</button></p>
          <div className="flex justify-center mb-4">
            <button type="submit" disabled={isSubmitting} className={`rounded-full text-white text-xl px-14 py-2 ${isSubmitting ? 'bg-mercury' : 'bg-primary'}`}>提交</button>
          </div>
        </form>
      </div>
    </>
  );
}

export default OTP;
